import React from "react";
import { Box, Button, Card, CardContent, Divider, Grid, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, DownloadOutlined, OpenInNewOutlined } from "@mui/icons-material";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../../providers/theme/colors/colors";
import {
  dayEventItems,
  dayEventsPdfPath,
  generalRulesPdfPath,
  onlineEvents,
  paymentDetails,
  pitchBattleLastDateLabel,
  posterSlides,
  registrationFormPdfPath,
  registrationContacts,
} from "../data";

type DycInfoPanelProps = {
  activeOnlineEvent: number;
  onOnlineEventChange: (value: number) => void;
  onOpenPitchBattle: () => void;
  formError: string;
  paymentDetailsRef?: React.RefObject<HTMLDivElement>;
  onScrollToForm?: () => void;
};

const actionButtonStyles = {
  borderRadius: 999,
};

export default function DycInfoPanel({
  activeOnlineEvent,
  onOnlineEventChange,
  onOpenPitchBattle,
  formError,
  paymentDetailsRef,
  onScrollToForm,
}: DycInfoPanelProps) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        border: `1px solid ${MAIN_THEME_COLOR}22`,
        boxShadow: "0 18px 42px rgba(9, 51, 84, 0.08)",
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5} height="100%">
              <Box
                sx={{
                  height: { xs: 420, md: 560 },
                  minHeight: { xs: 420, md: 560 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${SECONDARY_THEME_COLOR}55`,
                  backgroundColor: "#edf5ff",
                  boxShadow: "0 14px 34px rgba(9, 51, 84, 0.08)",
                }}
              >
                <Carousel
                  autoPlay
                  infiniteLoop
                  interval={3500}
                  showStatus={false}
                  showThumbs={false}
                  swipeable
                  emulateTouch
                  renderIndicator={(onClickHandler, isSelected, index, label) => (
                    <Box
                      key={`${label}-${index}`}
                      onClick={onClickHandler}
                      sx={{
                        display: "inline-block",
                        width: isSelected ? 28 : 10,
                        height: 10,
                        borderRadius: 999,
                        mx: 0.5,
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                        backgroundColor: isSelected ? MAIN_THEME_COLOR : "#d0deef",
                      }}
                    />
                  )}
                >
                  {posterSlides.map((poster) => (
                    <Box
                      key={poster.src}
                      sx={{
                        height: { xs: 420, md: 560 },
                        background:
                          "linear-gradient(180deg, rgba(247,251,255,1) 0%, rgba(232,243,255,1) 100%)",
                        p: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={poster.src}
                        alt={poster.alt}
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "contain",
                          borderRadius: 2.5,
                          backgroundColor: "#fff",
                        }}
                      />
                    </Box>
                  ))}
                </Carousel>
              </Box>

              <Box
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${MAIN_THEME_COLOR}18`,
                  backgroundColor: "#ffffff",
                  p: 2.5,
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      If queries regarding Registration Contact
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.75}>
                      Reach out to any of the contacts below.
                    </Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    {registrationContacts.map((contact) => (
                      <Grid item xs={12} sm={6} md={12} key={`${contact.name}-${contact.phone}`}>
                        <Box
                          sx={{
                            height: "100%",
                            borderRadius: 2,
                            border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                            backgroundColor: "#f8fbff",
                            px: 1.75,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={800}>
                            {contact.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mt={0.4}>
                            {contact.role}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} mt={0.8}>
                            {contact.phone}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Box>

              <Box
                ref={paymentDetailsRef}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${MAIN_THEME_COLOR}18`,
                  backgroundColor: "#ffffff",
                  p: 2.5,
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Typography variant="h6" fontWeight={800}>
                      Payment Details
                    </Typography>
                    {onScrollToForm && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={onScrollToForm}
                        sx={{
                          display: { xs: "inline-flex", md: "none" },
                          color: SECONDARY_THEME_COLOR,
                          fontWeight: 700,
                          textTransform: "none",
                          textDecoration: "underline",
                          p: 0,
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Scroll to Form ↓
                      </Button>
                    )}
                  </Stack>

                  {[
                    { label: "Name", value: paymentDetails.name },
                    { label: "UPI ID", value: paymentDetails.upiId },
                    { label: "Phone Number", value: paymentDetails.phone },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                        backgroundColor: "#f8fbff",
                        px: 1.75,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={800} mt={0.4}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      component="a"
                      href={paymentDetails.qrCodePath}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      startIcon={<OpenInNewOutlined />}
                      sx={{
                        ...actionButtonStyles,
                        backgroundColor: MAIN_THEME_COLOR,
                        "&:hover": {
                          backgroundColor: MAIN_THEME_COLOR,
                        },
                      }}
                    >
                      View QR Code
                    </Button>
                    <Button
                      component="a"
                      href={paymentDetails.qrCodePath}
                      download
                      variant="outlined"
                      startIcon={<DownloadOutlined />}
                      sx={actionButtonStyles}
                    >
                      Download QR Code
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#fff7ef",
                      border: "1px solid rgba(217, 119, 6, 0.24)",
                      px: 1.75,
                      py: 1.5,
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} color="#9a5a00">
                      Note: All payment must be made through digital mode itself
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${SECONDARY_THEME_COLOR}44`,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(246,250,255,1) 100%)",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
                <Stack spacing={2.5} height="100%">
                  <Box>
                    <Typography variant="h5" fontWeight={800}>
                      Convention Events
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.75}>
                      Online events brochures can be opened in a new tab or downloaded directly after you upload the PDF files.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${MAIN_THEME_COLOR}18`,
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={800}>
                        Online Events
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          aria-label="Previous Online Event"
                          onClick={() =>
                            onOnlineEventChange(
                              activeOnlineEvent > 0 ? activeOnlineEvent - 1 : onlineEvents.length - 1,
                            )
                          }
                          sx={{ border: `1px solid ${MAIN_THEME_COLOR}33`, color: MAIN_THEME_COLOR }}
                        >
                          <ChevronLeft />
                        </IconButton>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {activeOnlineEvent + 1} / {onlineEvents.length}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="Next Online Event"
                          onClick={() =>
                            onOnlineEventChange(
                              activeOnlineEvent < onlineEvents.length - 1 ? activeOnlineEvent + 1 : 0,
                            )
                          }
                          sx={{ border: `1px solid ${MAIN_THEME_COLOR}33`, color: MAIN_THEME_COLOR }}
                        >
                          <ChevronRight />
                        </IconButton>
                      </Stack>
                    </Box>

                    <Divider />

                    <Tabs
                      value={activeOnlineEvent}
                      onChange={(_, value) => onOnlineEventChange(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        px: 1,
                        "& .MuiTab-root": {
                          alignItems: "flex-start",
                          textTransform: "none",
                          fontWeight: 700,
                        },
                      }}
                    >
                      {onlineEvents.map((event, index) => (
                        <Tab key={event.id} label={`${index + 1}. ${event.title}`} />
                      ))}
                    </Tabs>

                    <Divider />

                    <Box sx={{ p: 2.5 }}>
                      <Stack spacing={2}>
                        <Typography variant="h6" fontWeight={800} color={MAIN_THEME_COLOR}>
                          {onlineEvents[activeOnlineEvent].title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {onlineEvents[activeOnlineEvent].description}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                          <Button
                            component="a"
                            href={onlineEvents[activeOnlineEvent].pdfPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            startIcon={<OpenInNewOutlined />}
                            sx={{
                              ...actionButtonStyles,
                              backgroundColor: MAIN_THEME_COLOR,
                              "&:hover": {
                                backgroundColor: MAIN_THEME_COLOR,
                              },
                            }}
                          >
                            View PDF
                          </Button>
                          <Button
                            component="a"
                            href={onlineEvents[activeOnlineEvent].pdfPath}
                            download
                            variant="outlined"
                            startIcon={<DownloadOutlined />}
                            sx={actionButtonStyles}
                          >
                            Download PDF
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${MAIN_THEME_COLOR}18`,
                      backgroundColor: "#ffffff",
                      p: 2.5,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          Day Events
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.75}>
                          These are the unit events for the convention day.
                        </Typography>
                      </Box>

                      <Grid container spacing={1.25}>
                        {dayEventItems.map((eventName, index) => (
                          <Grid item xs={12} sm={6} key={eventName}>
                            <Box
                              sx={{
                                height: "100%",
                                borderRadius: 2,
                                border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                                backgroundColor: "#f8fbff",
                                px: 1.5,
                                py: 1.25,
                              }}
                            >
                              <Typography variant="body2" fontWeight={700}>
                                {index + 1}. {eventName}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background:
                            "linear-gradient(180deg, rgba(245,249,255,1) 0%, rgba(255,255,255,1) 100%)",
                          border: `1px dashed ${SECONDARY_THEME_COLOR}`,
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" fontWeight={800} color={MAIN_THEME_COLOR}>
                            Rhythm and Roots Deanery Event
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            All the above events and the Rhythm and Roots deanery event use the same PDF.
                          </Typography>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <Button
                              component="a"
                              href={dayEventsPdfPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              startIcon={<OpenInNewOutlined />}
                              sx={{
                                ...actionButtonStyles,
                                backgroundColor: MAIN_THEME_COLOR,
                                "&:hover": {
                                  backgroundColor: MAIN_THEME_COLOR,
                                },
                              }}
                            >
                              View Day Events PDF
                            </Button>
                            <Button
                              component="a"
                              href={dayEventsPdfPath}
                              download
                              variant="outlined"
                              startIcon={<DownloadOutlined />}
                              sx={actionButtonStyles}
                            >
                              Download Day Events PDF
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Registration Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.75}>
                      All main fields and selected event fields are compulsory.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${MAIN_THEME_COLOR}18`,
                      backgroundColor: "#ffffff",
                      p: 2.5,
                    }}
                  >
                    <Stack spacing={2.25}>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          Registration Resources
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                          backgroundColor: "#f8fbff",
                          px: 1.75,
                          py: 1.5,
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle1" fontWeight={800}>
                            General Rules
                          </Typography>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <Button
                              component="a"
                              href={generalRulesPdfPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              startIcon={<OpenInNewOutlined />}
                              sx={{
                                ...actionButtonStyles,
                                backgroundColor: MAIN_THEME_COLOR,
                                "&:hover": {
                                  backgroundColor: MAIN_THEME_COLOR,
                                },
                              }}
                            >
                              View General Rules
                            </Button>
                            <Button
                              component="a"
                              href={generalRulesPdfPath}
                              download
                              variant="outlined"
                              startIcon={<DownloadOutlined />}
                              sx={actionButtonStyles}
                            >
                              Download General Rules
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                          backgroundColor: "#f8fbff",
                          px: 1.75,
                          py: 1.5,
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle1" fontWeight={800}>
                            Registration Form
                          </Typography>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <Button
                              component="a"
                              href={registrationFormPdfPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              startIcon={<OpenInNewOutlined />}
                              sx={{
                                ...actionButtonStyles,
                                backgroundColor: MAIN_THEME_COLOR,
                                "&:hover": {
                                  backgroundColor: MAIN_THEME_COLOR,
                                },
                              }}
                            >
                              View Registration Form
                            </Button>
                            <Button
                              component="a"
                              href={registrationFormPdfPath}
                              download
                              variant="outlined"
                              startIcon={<DownloadOutlined />}
                              sx={actionButtonStyles}
                            >
                              Download Registration Form
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${SECONDARY_THEME_COLOR}33`,
                          backgroundColor: "#fff7ef",
                          px: 1.75,
                          py: 1.5,
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle1" fontWeight={800}>
                            Pitch Battle Registration
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Register Pitch Battle separately. Last date: {pitchBattleLastDateLabel}.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Multiple entries are allowed from one unit.
                          </Typography>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <Button
                              variant="contained"
                              onClick={onOpenPitchBattle}
                              sx={{
                                ...actionButtonStyles,
                                backgroundColor: MAIN_THEME_COLOR,
                                "&:hover": {
                                  backgroundColor: MAIN_THEME_COLOR,
                                },
                              }}
                            >
                              Open Pitch Battle Form
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  {formError ? (
                    <Box>
                      <Typography color="error" variant="body2">
                        {formError}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
